import React, { useState, useCallback } from 'react';
import {
  Stack,
  Text,
  TextField,
  PrimaryButton,
  DefaultButton,
  MessageBar,
  MessageBarType,
  Spinner,
  SpinnerSize,
  ProgressIndicator,
  Toggle,
  Dropdown,
  IDropdownOption,
  Icon,
  Separator,
  Label,
  Link
} from '@fluentui/react';
// Schedule configuration will be added later when the components are available

interface FileServerConnectorSetupProps {
  isVisible: boolean;
  onDismiss: () => void;
  onComplete: (config: FileServerConfiguration) => Promise<void>;
}

interface FileServerConfiguration {
  serverPath: string;
  domain: string;
  username: string;
  password: string;
  indexName: string;
  aclEnabled: boolean;
  enableEmbeddings: boolean;
  maxFiles: number;
  fileExtensions: string[];
  excludePatterns: string[];
  chunkSize: number;
  chunkOverlap: number;
  // schedule will be added later when ScheduleConfiguration is available
}

const defaultFileExtensions = ['.pdf', '.docx', '.xlsx', '.pptx', '.txt', '.md', '.rtf', '.csv'];

const extensionOptions: IDropdownOption[] = [
  { key: '.pdf', text: 'PDF Documents (.pdf)' },
  { key: '.docx', text: 'Word Documents (.docx)' },
  { key: '.doc', text: 'Word Documents (.doc)' },
  { key: '.xlsx', text: 'Excel Spreadsheets (.xlsx)' },
  { key: '.xls', text: 'Excel Spreadsheets (.xls)' },
  { key: '.pptx', text: 'PowerPoint Presentations (.pptx)' },
  { key: '.ppt', text: 'PowerPoint Presentations (.ppt)' },
  { key: '.txt', text: 'Text Files (.txt)' },
  { key: '.md', text: 'Markdown Files (.md)' },
  { key: '.rtf', text: 'Rich Text Format (.rtf)' },
  { key: '.csv', text: 'CSV Files (.csv)' },
  { key: '.json', text: 'JSON Files (.json)' },
  { key: '.xml', text: 'XML Files (.xml)' },
  { key: '.html', text: 'HTML Files (.html)' },
  { key: '.htm', text: 'HTML Files (.htm)' }
];

export const FileServerConnectorSetup: React.FC<FileServerConnectorSetupProps> = ({
  isVisible,
  onDismiss,
  onComplete
}) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [config, setConfig] = useState<FileServerConfiguration>({
    serverPath: '',
    domain: '',
    username: '',
    password: '',
    indexName: '',
    aclEnabled: true, // Default to true for security
    enableEmbeddings: true,
    maxFiles: 10000,
    fileExtensions: defaultFileExtensions,
    excludePatterns: [],
    chunkSize: 1000,
    chunkOverlap: 200
  });
  
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState<{ type: MessageBarType; text: string } | null>(null);
  const [testProgress, setTestProgress] = useState<number | undefined>(undefined);
  const [discoveredShares, setDiscoveredShares] = useState<any[]>([]);

  const clearMessage = useCallback(() => setMessage(null), []);

  const validateStep = (step: number): boolean => {
    switch (step) {
      case 1:
        return !!config.serverPath && !!config.domain && !!config.username && !!config.password;
      case 2:
        return !!config.indexName;
      case 3:
        return config.fileExtensions.length > 0;
      case 4:
        return config.maxFiles > 0 && config.chunkSize > 0;
      case 5:
        return true; // Optional ACL configuration
      case 6:
        return true; // Optional scheduling
      default:
        return false;
    }
  };

  const testConnection = async () => {
    setIsLoading(true);
    setTestProgress(0);
    clearMessage();

    try {
      // Simulate connection testing steps
      const steps = [
        'Resolving domain controller',
        'Testing Active Directory authentication',
        'Verifying file server access',
        'Checking permissions'
      ];

      for (let i = 0; i < steps.length; i++) {
        setTestProgress((i + 1) / steps.length * 100);
        await new Promise(resolve => setTimeout(resolve, 1000)); // Simulate async work
      }

      setMessage({
        type: MessageBarType.success,
        text: `Successfully connected to ${config.domain}\\${config.serverPath}. Authentication and file access verified.`
      });
    } catch (error) {
      setMessage({
        type: MessageBarType.error,
        text: `Connection failed: ${error instanceof Error ? error.message : 'Unknown error'}`
      });
    } finally {
      setIsLoading(false);
      setTestProgress(undefined);
    }
  };

  const discoverShares = async () => {
    setIsLoading(true);
    clearMessage();

    try {
      // Simulate share discovery
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      const mockShares = [
        { name: 'Documents', path: `${config.serverPath}/Documents`, description: 'Shared Documents' },
        { name: 'Projects', path: `${config.serverPath}/Projects`, description: 'Project Files' },
        { name: 'Archive', path: `${config.serverPath}/Archive`, description: 'Archived Files' }
      ];
      
      setDiscoveredShares(mockShares);
      setMessage({
        type: MessageBarType.success,
        text: `Discovered ${mockShares.length} available shares`
      });
    } catch (error) {
      setMessage({
        type: MessageBarType.error,
        text: `Share discovery failed: ${error instanceof Error ? error.message : 'Unknown error'}`
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    setIsLoading(true);
    clearMessage();

    try {
      if (onComplete) {
        await onComplete(config);
      }
      
      setMessage({
        type: MessageBarType.success,
        text: 'File Server connector configuration saved successfully!'
      });
      
      // Close after successful save
      setTimeout(onDismiss, 2000);
    } catch (error) {
      setMessage({
        type: MessageBarType.error,
        text: `Failed to save configuration: ${error instanceof Error ? error.message : 'Unknown error'}`
      });
    } finally {
      setIsLoading(false);
    }
  };

  const renderStep1 = () => (
    <Stack tokens={{ childrenGap: 16 }}>
      <Text variant="xLarge">📁 File Server Connection</Text>
      <Text>Configure connection to your Microsoft File Server with Active Directory authentication.</Text>
      
      <TextField
        label="Server Path (UNC Format)"
        placeholder="\\\\server\\share or //server/share"
        value={config.serverPath}
        onChange={(_, value) => setConfig(prev => ({ ...prev, serverPath: value || '' }))}
        required
        description="Enter the UNC path to your file server share"
      />
      
      <TextField
        label="Domain"
        placeholder="DOMAIN or domain.com"
        value={config.domain}
        onChange={(_, value) => setConfig(prev => ({ ...prev, domain: value || '' }))}
        required
        description="Active Directory domain name"
      />
      
      <TextField
        label="Username"
        placeholder="domain\\username"
        value={config.username}
        onChange={(_, value) => setConfig(prev => ({ ...prev, username: value || '' }))}
        required
        description="Domain user with file server access"
      />
      
      <TextField
        label="Password"
        type="password"
        value={config.password}
        onChange={(_, value) => setConfig(prev => ({ ...prev, password: value || '' }))}
        required
        canRevealPassword
        description="Domain user password"
      />
      
      {config.serverPath && config.domain && config.username && config.password && (
        <Stack tokens={{ childrenGap: 8 }}>
          <PrimaryButton
            text="Test Connection"
            onClick={testConnection}
            disabled={isLoading}
            iconProps={{ iconName: 'PlugConnected' }}
          />
          
          <DefaultButton
            text="Discover Shares"
            onClick={discoverShares}
            disabled={isLoading}
            iconProps={{ iconName: 'FabricFolder' }}
          />
        </Stack>
      )}
      
      {testProgress !== undefined && (
        <ProgressIndicator
          label="Testing connection..."
          percentComplete={testProgress / 100}
        />
      )}
      
      {discoveredShares.length > 0 && (
        <Stack tokens={{ childrenGap: 8 }}>
          <Label>Discovered Shares:</Label>
          {discoveredShares.map(share => (
            <Stack key={share.name} horizontal tokens={{ childrenGap: 8 }} styles={{ root: { padding: 8, border: '1px solid #e1e1e1', borderRadius: 4 } }}>
              <Icon iconName="FabricFolder" />
              <Stack>
                <Text variant="medium" styles={{ root: { fontWeight: 600 } }}>{share.name}</Text>
                <Text variant="small">{share.path}</Text>
                <Text variant="small" styles={{ root: { color: '#666' } }}>{share.description}</Text>
              </Stack>
              <DefaultButton
                text="Select"
                onClick={() => setConfig(prev => ({ ...prev, serverPath: share.path }))}
                styles={{ root: { marginLeft: 'auto' } }}
              />
            </Stack>
          ))}
        </Stack>
      )}
    </Stack>
  );

  const renderStep2 = () => (
    <Stack tokens={{ childrenGap: 16 }}>
      <Text variant="xLarge">🔍 Search Index Configuration</Text>
      <Text>Configure the Azure Search index for your file server content.</Text>
      
      <TextField
        label="Index Name"
        placeholder="fileserver-documents-index"
        value={config.indexName}
        onChange={(_, value) => setConfig(prev => ({ ...prev, indexName: value || '' }))}
        required
        description="A new search index will be created with support for file server documents, metadata, ACLs, and vector search."
      />
      
      <MessageBar messageBarType={MessageBarType.info}>
        <Text>
          <Icon iconName="Info" style={{ marginRight: 8 }} />
          The index will be optimized for file server content with fields for file paths, server names, 
          ACL permissions, and document content with vector embeddings for semantic search.
        </Text>
      </MessageBar>
    </Stack>
  );

  const renderStep3 = () => (
    <Stack tokens={{ childrenGap: 16 }}>
      <Text variant="xLarge">📄 File Type Configuration</Text>
      <Text>Select which file types to process and index.</Text>
      
      <Dropdown
        label="File Extensions to Process"
        multiSelect
        options={extensionOptions}
        selectedKeys={config.fileExtensions}
        onChange={(_, option) => {
          if (option) {
            const newExtensions = option.selected
              ? [...config.fileExtensions, option.key as string]
              : config.fileExtensions.filter(ext => ext !== option.key);
            setConfig(prev => ({ ...prev, fileExtensions: newExtensions }));
          }
        }}
        placeholder="Select file types to process"
      />
      
      <TextField
        label="Exclude Patterns (Optional)"
        placeholder="temp, backup, .tmp, ~$"
        value={config.excludePatterns.join(', ')}
        onChange={(_, value) => {
          const patterns = value ? value.split(',').map(p => p.trim()).filter(p => p) : [];
          setConfig(prev => ({ ...prev, excludePatterns: patterns }));
        }}
        description="Comma-separated list of patterns to exclude from indexing"
      />
      
      <MessageBar messageBarType={MessageBarType.info}>
        Selected file types: {config.fileExtensions.join(', ')}
      </MessageBar>
    </Stack>
  );

  const renderStep4 = () => (
    <Stack tokens={{ childrenGap: 16 }}>
      <Text variant="xLarge">⚙️ Processing Configuration</Text>
      <Text>Configure document processing and chunking settings.</Text>
      
      <TextField
        label="Maximum Files to Process"
        type="number"
        value={config.maxFiles.toString()}
        onChange={(_, value) => setConfig(prev => ({ ...prev, maxFiles: parseInt(value || '10000') }))}
        description="Limit the number of files to process in a single job"
      />
      
      <TextField
        label="Chunk Size (characters)"
        type="number"
        value={config.chunkSize.toString()}
        onChange={(_, value) => setConfig(prev => ({ ...prev, chunkSize: parseInt(value || '1000') }))}
        description="Size of text chunks for processing large documents"
      />
      
      <TextField
        label="Chunk Overlap (characters)"
        type="number"
        value={config.chunkOverlap.toString()}
        onChange={(_, value) => setConfig(prev => ({ ...prev, chunkOverlap: parseInt(value || '200') }))}
        description="Number of overlapping characters between chunks"
      />
      
      <Toggle
        label="Enable Vector Embeddings"
        checked={config.enableEmbeddings}
        onChange={(_, checked) => setConfig(prev => ({ ...prev, enableEmbeddings: checked || false }))}
        onText="Enabled - Better semantic search"
        offText="Disabled - Faster processing"
      />
    </Stack>
  );

  const renderStep5 = () => (
    <Stack tokens={{ childrenGap: 16 }}>
      <Text variant="xLarge">🔐 Access Control Configuration</Text>
      <Text>Configure Active Directory ACL extraction and security filtering.</Text>
      
      <Toggle
        label="Enable Access Control Lists (ACL)"
        checked={config.aclEnabled}
        onChange={(_, checked) => setConfig(prev => ({ ...prev, aclEnabled: checked || false }))}
        onText="Security permissions preserved"
        offText="All content publicly searchable"
      />
      
      <MessageBar messageBarType={config.aclEnabled ? MessageBarType.success : MessageBarType.warning}>
        {config.aclEnabled ? (
          <Text>
            <Icon iconName="Shield" style={{ marginRight: 8 }} />
            ACL extraction enabled: File permissions will be extracted from Active Directory and 
            search results will respect user access rights.
          </Text>
        ) : (
          <Text>
            <Icon iconName="Warning" style={{ marginRight: 8 }} />
            ACL extraction disabled: All indexed content will be searchable by all users. 
            Use this only for public content.
          </Text>
        )}
      </MessageBar>
      
      {config.aclEnabled && (
        <Stack tokens={{ childrenGap: 8 }}>
          <Text variant="medium" styles={{ root: { fontWeight: 600 } }}>ACL Features:</Text>
          <Stack tokens={{ childrenGap: 4 }}>
            <Text>• Extract file-level permissions from Windows ACLs</Text>
            <Text>• Map domain users and groups to search permissions</Text>
            <Text>• Respect inherited folder permissions</Text>
            <Text>• Support for complex Active Directory group hierarchies</Text>
          </Stack>
        </Stack>
      )}
    </Stack>
  );

  const renderStep6 = () => (
    <Stack tokens={{ childrenGap: 16 }}>
      <Text variant="xLarge">⏰ Schedule Configuration</Text>
      <Text>Set up automatic crawling schedule for your file server.</Text>
      
      <MessageBar messageBarType={MessageBarType.info}>
        <Text>
          <Icon iconName="Info" style={{ marginRight: 8 }} />
          Schedule configuration will be available in a future update. For now, the connector will run manually when triggered.
        </Text>
      </MessageBar>
    </Stack>
  );

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 1: return renderStep1();
      case 2: return renderStep2();
      case 3: return renderStep3();
      case 4: return renderStep4();
      case 5: return renderStep5();
      case 6: return renderStep6();
      default: return null;
    }
  };

  if (!isVisible) return null;

  return (
    <Stack tokens={{ childrenGap: 24 }} styles={{ root: { padding: 24, maxWidth: 800 } }}>
      <Stack horizontal horizontalAlign="space-between" verticalAlign="center">
        <Text variant="xxLarge">🖥️ Microsoft File Server Connector</Text>
        <DefaultButton text="Close" onClick={onDismiss} iconProps={{ iconName: 'Cancel' }} />
      </Stack>

      <Text>Connect to Microsoft File Servers with Active Directory authentication and ACL support.</Text>

      {message && (
        <MessageBar messageBarType={message.type} onDismiss={clearMessage}>
          {message.text}
        </MessageBar>
      )}

      <Stack horizontal tokens={{ childrenGap: 16 }}>
        {[1, 2, 3, 4, 5, 6].map(step => (
          <Stack key={step} horizontalAlign="center" tokens={{ childrenGap: 4 }}>
            <div
              style={{
                width: 32,
                height: 32,
                borderRadius: '50%',
                backgroundColor: step === currentStep ? '#0078d4' : step < currentStep ? '#107c10' : '#e1e1e1',
                color: step <= currentStep ? 'white' : '#666',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 14,
                fontWeight: 600,
                cursor: step < currentStep ? 'pointer' : 'default'
              }}
              onClick={() => step < currentStep && setCurrentStep(step)}
            >
              {step < currentStep ? '✓' : step}
            </div>
            <Text variant="small" styles={{ root: { textAlign: 'center', maxWidth: 80 } }}>
              {step === 1 && 'Connection'}
              {step === 2 && 'Index'}
              {step === 3 && 'File Types'}
              {step === 4 && 'Processing'}
              {step === 5 && 'Security'}
              {step === 6 && 'Schedule'}
            </Text>
          </Stack>
        ))}
      </Stack>

      <Separator />

      {renderCurrentStep()}

      <Separator />

      <Stack horizontal horizontalAlign="space-between">
        <DefaultButton
          text="Previous"
          onClick={() => setCurrentStep(prev => Math.max(1, prev - 1))}
          disabled={currentStep === 1 || isLoading}
          iconProps={{ iconName: 'ChevronLeft' }}
        />
        
        <Stack horizontal tokens={{ childrenGap: 8 }}>
          {currentStep < 6 ? (
            <PrimaryButton
              text="Next"
              onClick={() => setCurrentStep(prev => prev + 1)}
              disabled={!validateStep(currentStep) || isLoading}
              iconProps={{ iconName: 'ChevronRight' }}
            />
          ) : (
            <PrimaryButton
              text={isLoading ? 'Saving...' : 'Save Configuration'}
              onClick={handleSave}
              disabled={isLoading || !validateStep(currentStep)}
              iconProps={{ iconName: isLoading ? undefined : 'Save' }}
            >
              {isLoading && <Spinner size={SpinnerSize.xSmall} styles={{ root: { marginRight: 8 } }} />}
            </PrimaryButton>
          )}
        </Stack>
      </Stack>

      <Stack tokens={{ childrenGap: 8 }}>
        <Text variant="small" styles={{ root: { color: '#666' } }}>
          Step {currentStep} of 6 | Microsoft File Server Connector with AD ACL Support
        </Text>
        
        <Text variant="small" styles={{ root: { color: '#666' } }}>
          💡 <strong>Tip:</strong> Test your connection in Step 1 to verify domain authentication and file access before proceeding.
        </Text>
      </Stack>
    </Stack>
  );
};